import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { BACKUPS_KEY, backupsApi } from '@/lib/backups-api';
import { getErrorMessage } from '@/lib/errors';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { BackupActivity } from '@/modules/backups/BackupActivity';
import { BackupsSummary } from '@/modules/backups/BackupsSummary';
import { DestinationDialog } from '@/modules/backups/DestinationDialog';
import { DestinationsSection } from '@/modules/backups/DestinationsSection';
import { backupHealth, type HealthAction } from '@/modules/backups/health';
import { HealthStrip } from '@/modules/backups/HealthStrip';
import { ErrorToast, LoadError, PageSkeleton } from '@/modules/backups/PageStates';
import { RestoreDialog } from '@/modules/backups/RestoreDialog';
import { RunBackupDialog } from '@/modules/backups/RunBackupDialog';
import { ScheduleDialog } from '@/modules/backups/ScheduleDialog';
import { SchedulesSection } from '@/modules/backups/SchedulesSection';
import { scopeFor } from '@/modules/backups/scope';
import { SourceDialog } from '@/modules/backups/SourceDialog';
import { SourcesSection } from '@/modules/backups/SourcesSection';
import { flatPrimary, hairline } from '@/components/surface/tones';
import { Panel } from '@/components/surface/ui';
import { cn } from '@/lib/utils';
import { VerificationPanel } from '@/modules/backups/VerificationPanel';
import type { BackupRecord, BackupScope, BackupSource } from '@/types/api';

/**
 * Backups: what is copied, where it goes, and how to put it back.
 *
 * ## Three things this page has to be honest about
 *
 * 1. **A local destination is not a backup of the server.** It survives a bad change and a deleted
 *    table; it does not survive the disk, the VPS or the provider. Said plainly on every local
 *    destination, because somebody who assumes otherwise finds out on the worst day.
 *
 * 2. **A failed backup stays in the list.** "The last three nights failed" is the most important
 *    thing this page can say, and hiding failures would leave the newest *success* at the top with
 *    nothing to indicate it is a fortnight old.
 *
 * 3. **A schedule that is producing nothing is flagged.** `is_stale` means switched on but not
 *    delivering — a stopped scheduler or an unreachable server — which otherwise looks identical to
 *    one that is working.
 *
 * ## How the page is laid out
 *
 * In the order a customer's questions come. Is everything all right: one health state with its
 * reasons, each reason a link to the fix. How do things stand: four figures of equal weight. Is it
 * checked and set up: verification and schedules side by side, then one block holding what is backed
 * up beside where it goes. And last, what happened: the backup history, which is a record rather than
 * a status — the latest five rows show without opening anything, and the rest waits behind "View all
 * history".
 *
 * An account with nothing set up yet gets its setup steps as the health reasons (add a source, add
 * storage, add a schedule, take a backup), each one a button, rather than a separate guide repeating
 * them.
 *
 * This component owns every request and every rule about when an action is allowed; the sections
 * below it only draw.
 */
export function BackupsPage() {
  const queryClient = useQueryClient();
  const { permitsChanges } = useSubscriptionAccess();

  const [destinationOpen, setDestinationOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [sourceDialog, setSourceDialog] = useState<{ open: boolean; source: BackupSource | null }>({
    open: false,
    source: null,
  });
  const [runSource, setRunSource] = useState('');
  const [runDestination, setRunDestination] = useState('');
  const [runScope, setRunScope] = useState<BackupScope>('full');
  const [scheduleSource, setScheduleSource] = useState('');
  const [scheduleDestination, setScheduleDestination] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [retention, setRetention] = useState(7);
  const [scheduleScope, setScheduleScope] = useState<BackupScope>('full');
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<BackupRecord | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const overview = useQuery({
    queryKey: BACKUPS_KEY,
    queryFn: () => backupsApi.overview(),
    // Polls while something is in flight: a backup, or a restore test running on the worker. Both
    // finish on their own, and the customer should see the result arrive without reloading.
    refetchInterval: (query) =>
      (query.state.data?.backups ?? []).some((b) => b.status === 'running') ||
      query.state.data?.verification.summary.running
        ? 5000
        : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: BACKUPS_KEY });
  };

  /*
   * Takes a backup now. The values travel as the mutation's variables rather than being read from
   * state inside it, so the "Backing up…" row can show what is actually running even if the dialog
   * is reopened and the pickers changed while it runs.
   */
  const runNow = useMutation({
    mutationFn: (input: { sourceId: string; destinationId: string; scope: BackupScope }) =>
      backupsApi.runNow(input.sourceId, input.destinationId, input.scope),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  /**
   * Downloading a backup.
   *
   * The server decides where the bytes come from — the customer's own bucket for cloud storage, or
   * through the platform for one kept on the server — and either way the answer is a URL to
   * navigate to. A navigation rather than a fetch because the response is a file: fetching it would
   * pull the whole archive into the tab's memory before saving anything, which for a 2 GB backup is
   * how the tab dies.
   */
  const download = useMutation({
    mutationFn: ({ id, format }: { id: string; format: 'tar' | 'zip' }) =>
      backupsApi.downloadLink(id, format),
    onSuccess: (result) => {
      setError(null);
      window.location.href = result.url;
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  const data = overview.data;
  const sources = data?.sources ?? [];

  const setSchedule = useMutation({
    mutationFn: () =>
      backupsApi.setSchedule({
        source_id: scheduleSource,
        destination_id: scheduleDestination,
        frequency,
        retention_count: retention,
        scope: scopeFor(
          sources.find((source) => source.id === scheduleSource),
          data?.schedules.find((schedule) => schedule.source_id === scheduleSource)?.scope ?? scheduleScope,
        ),
      }),
    onSuccess: () => {
      setError(null);
      setScheduleSource('');
      setScheduleOpen(false);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  const toggleSchedule = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      backupsApi.setScheduleEnabled(id, enabled),
    onSuccess: invalidate,
  });

  const removeSchedule = useMutation({
    mutationFn: (id: string) => backupsApi.removeSchedule(id),
    onSuccess: invalidate,
  });

  const removeSource = useMutation({
    mutationFn: (id: string) => backupsApi.removeSource(id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  const removeDestination = useMutation({
    mutationFn: (id: string) => backupsApi.removeDestination(id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  const removeBackup = useMutation({
    mutationFn: (id: string) => backupsApi.removeBackup(id),
    onSuccess: invalidate,
  });

  const testRestore = useMutation({
    mutationFn: (id: string) => backupsApi.testRestore(id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
  });

  /*
   * Only a source on a server that is still on the account can be backed up.
   *
   * The reason has to be *said*, not left to be inferred from an empty dropdown: a picker with
   * nothing in it and no explanation reads as a broken page rather than as a step not yet taken.
   */
  const usable = sources.filter((source) => source.server_name !== null);
  const orphaned = sources.length - usable.length;

  const pickerHint =
    sources.length === 0
      ? 'You have no backup sources yet. Add one first: a directory, a database or both on one of your servers.'
      : usable.length === 0
        ? 'None of your sources is on a server that is still on your account. Edit a source to choose its server.'
        : orphaned > 0
          ? `${orphaned} source${orphaned === 1 ? ' is' : 's are'} not listed because ${orphaned === 1 ? 'its server is' : 'their servers are'} no longer on your account.`
          : null;

  // One restore test per source at a time, which the server enforces too: two would load two
  // scratch copies of the same database side by side.
  const testingSources = new Set(
    (data?.verification.history ?? [])
      .filter((v) => v.kind === 'restore' && v.status === 'running')
      .map((v) => v.source_id),
  );

  const running = runNow.isPending ? runNow.variables : undefined;
  const pendingRun =
    running === undefined
      ? null
      : {
          source: sources.find((s) => s.id === running.sourceId)?.name ?? 'the source',
          destination: data?.destinations.find((d) => d.id === running.destinationId)?.name ?? 'the destination',
        };

  // A dialog shows its own error while it is open; the corner message is for everything else.
  const openSchedule = (open: boolean) => {
    setError(null);
    setScheduleOpen(open);
  };

  const openSource = (source: BackupSource | null) => {
    setError(null);
    setSourceDialog({ open: true, source });
  };

  /** A health reason's link: the same dialog or section the page already has for it. */
  const onHealthAction = (action: HealthAction) => {
    switch (action.kind) {
      case 'jump':
        // A failed backup the health panel points at may be older than the rows the history shows
        // by default, so following that link opens the full history first.
        if (action.section === 'activity') {
          setHistoryExpanded(true);
        }

        document.getElementById(action.section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'add-source':
        openSource(null);
        break;
      case 'add-destination':
        setDestinationOpen(true);
        break;
      case 'new-schedule':
        openSchedule(true);
        break;
      case 'back-up-now':
        setRunOpen(true);
        break;
    }
  };

  return (
    // The light theme's secondary text is a step darker on this page: against white cards with a
    // grey inner surface, the shared value read as washed out. The dark theme keeps its own value.
    <div className="space-y-6 [--muted-foreground:215_20%_34%] dark:[--muted-foreground:215_20%_65%]">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Backups</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Copies of the directories and databases on your servers, kept where you choose, and proved
            to restore. The platform never stores them itself.
          </p>
        </div>
        <Button
          onClick={() => setRunOpen(true)}
          disabled={!permitsChanges || runNow.isPending}
          className={`w-full sm:ml-auto sm:w-auto ${flatPrimary}`}
        >
          {runNow.isPending ? <Loader2 className="animate-spin" /> : <Play />}
          Back up now
        </Button>
      </header>

      <SubscriptionNotice />

      {overview.isLoading && <PageSkeleton />}

      {overview.isError && data === undefined && (
        <LoadError message={getErrorMessage(overview.error)} onRetry={() => void overview.refetch()} />
      )}

      {data && (
        <>
          <HealthStrip health={backupHealth(data)} permitsChanges={permitsChanges} onAction={onHealthAction} />

          <BackupsSummary data={data} />

          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
            <VerificationPanel verification={data.verification} />
            <SchedulesSection
              schedules={data.schedules}
              destinations={data.destinations}
              permitsChanges={permitsChanges}
              busy={{ toggle: toggleSchedule.isPending, remove: removeSchedule.isPending }}
              onNew={() => openSchedule(true)}
              onToggle={(id, enabled) => toggleSchedule.mutate({ id, enabled })}
              onRemove={(id) => removeSchedule.mutate(id)}
            />
          </div>

          {/* What is backed up and where it goes, as one block: the two halves of one question.
              Side by side from lg up, stacked with a hairline between them below that. */}
          <Panel label="What is backed up, and where it is stored" className="grid grid-cols-1 lg:grid-cols-2">
            <SourcesSection
              sources={data.sources}
              schedules={data.schedules}
              permitsChanges={permitsChanges}
              removing={removeSource.isPending}
              onAdd={() => openSource(null)}
              onEdit={(source) => openSource(source)}
              onRemove={(id) => removeSource.mutate(id)}
            />

            <DestinationsSection
              className={cn('border-t lg:border-l lg:border-t-0', hairline)}
              destinations={data.destinations}
              schedules={data.schedules}
              permitsChanges={permitsChanges}
              removing={removeDestination.isPending}
              onAdd={() => setDestinationOpen(true)}
              onRemove={(id) => removeDestination.mutate(id)}
            />
          </Panel>

          <BackupActivity
            backups={data.backups}
            destinations={data.destinations}
            pendingRun={pendingRun}
            permitsChanges={permitsChanges}
            // Absent from an older API response, which is a plan that has not been asked about
            // rather than one that refuses: the server still decides, and says so if it refuses.
            canDownload={data.allowance?.can_download ?? true}
            testingSources={testingSources}
            expanded={historyExpanded}
            onExpandedChange={setHistoryExpanded}
            busy={{
              download: download.isPending,
              testRestore: testRestore.isPending,
              forget: removeBackup.isPending,
            }}
            onDownload={(id, format) => download.mutate({ id, format })}
            onTestRestore={(id) => testRestore.mutate(id)}
            onRestore={setRestoring}
            onForget={(id) => removeBackup.mutate(id)}
          />
        </>
      )}

      <RunBackupDialog
        open={runOpen}
        onOpenChange={setRunOpen}
        sources={usable}
        destinations={data?.destinations ?? []}
        sourceId={runSource}
        destinationId={runDestination}
        scope={runScope}
        onSourceChange={setRunSource}
        onDestinationChange={setRunDestination}
        onScopeChange={setRunScope}
        pickerHint={pickerHint}
        canSubmit={permitsChanges && !runNow.isPending && runSource !== '' && runDestination !== ''}
        onSubmit={() =>
          runNow.mutate({
            sourceId: runSource,
            destinationId: runDestination,
            scope: scopeFor(usable.find((source) => source.id === runSource), runScope),
          })
        }
      />

      <ScheduleDialog
        open={scheduleOpen}
        onOpenChange={openSchedule}
        sources={usable}
        destinations={data?.destinations ?? []}
        schedules={data?.schedules ?? []}
        sourceId={scheduleSource}
        destinationId={scheduleDestination}
        scope={scheduleScope}
        frequency={frequency}
        retention={retention}
        onSourceChange={setScheduleSource}
        onDestinationChange={setScheduleDestination}
        onScopeChange={setScheduleScope}
        onFrequencyChange={setFrequency}
        onRetentionChange={setRetention}
        pickerHint={pickerHint}
        error={error}
        pending={setSchedule.isPending}
        canSubmit={permitsChanges && !setSchedule.isPending && scheduleSource !== '' && scheduleDestination !== ''}
        onSubmit={() => setSchedule.mutate()}
      />

      <SourceDialog
        open={sourceDialog.open}
        source={sourceDialog.source}
        hasBackups={
          sourceDialog.source !== null && (data?.backups ?? []).some((backup) => backup.source_id === sourceDialog.source?.id)
        }
        onOpenChange={(open) => setSourceDialog((current) => ({ ...current, open }))}
        onSaved={() => {
          setSourceDialog({ open: false, source: null });
          invalidate();
        }}
      />

      <DestinationDialog
        open={destinationOpen}
        onOpenChange={setDestinationOpen}
        onDone={() => {
          setDestinationOpen(false);
          invalidate();
        }}
      />

      <RestoreDialog
        backup={restoring}
        isOffsite={(data?.destinations ?? []).find((d) => d.id === restoring?.destination_id)?.is_offsite ?? false}
        currentServerId={sources.find((source) => source.id === restoring?.source_id)?.server_id ?? null}
        onClose={() => setRestoring(null)}
        onRestored={invalidate}
      />

      <ErrorToast message={scheduleOpen || sourceDialog.open ? null : error} onDismiss={() => setError(null)} />
    </div>
  );
}
